import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import paymentsApi from '../../services/paymentsApi';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const HIGH_DOLLAR = 5000;

const BUCKETS = [
  { key: 'submitted_to_finance', title: 'Awaiting review', severity: 'warning' },
  { key: 'finance_review', title: 'In review', severity: 'info' },
  { key: 'returned_to_operations', title: 'Returned to Operations', severity: 'default' },
  { key: 'finance_approved', title: 'Approved — not yet exported', severity: 'success' },
];

function BucketTable({ batches, onOpen }) {
  if (batches.length === 0) return <Alert severity="info" sx={{ mb: 2 }}>None.</Alert>;
  return (
    <Table size="small" sx={{ mb: 3 }}>
      <TableHead>
        <TableRow>
          <TableCell>Batch</TableCell>
          <TableCell>Invoice week</TableCell>
          <TableCell align="right">Rows</TableCell>
          <TableCell align="right">Flagged</TableCell>
          <TableCell align="right">Calculated</TableCell>
          <TableCell align="right">Approved</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {batches.map((b) => {
          const high = Number(b.total_approved_amount || b.total_calculated_amount || 0) >= HIGH_DOLLAR;
          return (
            <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(b.id)}>
              <TableCell>
                {b.batch_number}
                {high && <Chip size="small" color="error" label="high $" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>{b.invoice_week || '—'}</TableCell>
              <TableCell align="right">{b.total_rows}</TableCell>
              <TableCell align="right">{b.flagged_rows}</TableCell>
              <TableCell align="right">{money(b.total_calculated_amount)}</TableCell>
              <TableCell align="right">{money(b.total_approved_amount)}</TableCell>
              <TableCell align="right"><Button size="small">Open</Button></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function FinanceDashboard() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // One call per bucket status (server filters by status).
      const results = await Promise.all(BUCKETS.map((b) => paymentsApi.listBatches({ status: b.key })));
      const merged = [];
      results.forEach((list, i) => merged.push(...list.map((b) => ({ ...b, _bucket: BUCKETS[i].key }))));
      setBatches(merged);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (id) => navigate(`/payments/batches/${id}`);

  const totalPending = batches
    .filter((b) => b._bucket === 'submitted_to_finance' || b._bucket === 'finance_review')
    .reduce((s, b) => s + Number(b.total_calculated_amount || 0), 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4">Finance Review</Typography>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pending review total (awaiting + in review): <strong>{money(totalPending)}</strong>
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
        ) : (
          BUCKETS.map((bucket) => (
            <Box key={bucket.key}>
              <Typography variant="h6" sx={{ mb: 1 }}>{bucket.title}</Typography>
              <BucketTable batches={batches.filter((b) => b._bucket === bucket.key)} onOpen={open} />
            </Box>
          ))
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default FinanceDashboard;
